/**
 * Reading a panel, and making a change — SPEC §3.4.
 *
 * `fetch`, not axios. Reading `X-Stream-Head` off a response and counting attempts to exactly two
 * is clearer written out than configured, and those two things are the whole of what this file
 * does that a fetch wrapper would not.
 *
 * **Exactly two attempts, and the second one is the reader's.** A timeout or a 5xx on a GET is one
 * state — re-reading is free — so the first failure sets `unreachable` and renders the server's
 * noun in the browser's sentence. *Retry* makes the second attempt; its failure is
 * `unreachable again`, and **there is no third**. A silent third retry is how a page ends up
 * showing figures nobody can date. That counting lives in the store, which owns `attempts`; this
 * file makes one attempt per call and says plainly how it failed.
 */

import type { Who } from "./auth";
import type { Refused } from "./types/state";

const BASE = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");
const TIMEOUT_MS = 20_000;

export const api = (path: string) => `${BASE}/api${path}`;

/** How a read or a write failed, in the four words the store and the panels branch on. */
export type Trouble =
  | { kind: "unreachable" } // no answer at all: a timeout, a dead socket, a DNS failure
  | { kind: "broke"; says: string } // a 5xx that ANSWERED, so it was alive to write a sentence
  | { kind: "refused"; status: number; refusal: Refused } // 404/409/422 — a named state
  | { kind: "signed out"; refusal: Refused }; // a 401 anywhere: route to /signin in its state

export class Failed extends Error {
  readonly trouble: Trouble;
  constructor(trouble: Trouble) {
    super(trouble.kind);
    this.name = "Failed";
    this.trouble = trouble;
  }
}

/**
 * The sentences the browser composes, and nothing else. They are compiled in because the moment
 * they are needed is the moment the server cannot be reached. **No other sentence is ever
 * composed here**: everything about the day is the server's `says`.
 */
const BROWSER_SAYS = {
  unreachable: "Couldn't load {noun}: the server didn't answer.",
  "unreachable again":
    "Couldn't load {noun} on a second try: the server still isn't answering. Everything on " +
    "screen is from before it stopped.",
  "post timed out":
    "{act} may or may not have been applied: the server didn't answer in time. Re-read the " +
    "page before trying again.",
} as const;

/** One of the browser's sentences, with `{noun}` or `{act}` filled. */
export function browserSays(
  name: keyof typeof BROWSER_SAYS,
  fill: { noun?: string; act?: string },
): string {
  return BROWSER_SAYS[name]
    .replace("{noun}", fill.noun ?? "this panel")
    .replace("{act}", fill.act ?? "That change");
}

async function send(path: string, init: RequestInit): Promise<Response> {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), TIMEOUT_MS);
  try {
    return await fetch(api(path), { ...init, credentials: "include", signal: abort.signal });
  } catch {
    // A network error and a timeout are the same fact to the reader: no answer came.
    throw new Failed({ kind: "unreachable" });
  } finally {
    clearTimeout(timer);
  }
}

async function body(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { says: text };
  }
}

function asRefusal(parsed: unknown, fallback: string): Refused {
  const it = (parsed ?? {}) as Partial<Refused>;
  return {
    state: it.state ?? fallback,
    says: it.says ?? fallback,
    fields: it.fields ?? null,
  };
}

async function trouble(response: Response): Promise<never> {
  const parsed = await body(response);
  if (response.status === 401) {
    // A 401 on any request routes to /signin in the matching state, and the page renders the
    // server's own `says`. A1 *session ended*'s sentence is the server's; we do not paraphrase it.
    throw new Failed({ kind: "signed out", refusal: asRefusal(parsed, "session ended") });
  }
  if (response.status >= 500) {
    // It answered, so it was alive to write a sentence: render that, not our own.
    const says =
      ((parsed ?? {}) as { says?: string }).says ??
      "The server couldn't build that. Nothing on screen has changed.";
    throw new Failed({ kind: "broke", says });
  }
  throw new Failed({
    kind: "refused",
    status: response.status,
    refusal: asRefusal(parsed, "refused"),
  });
}

/**
 * One GET. `head` is the `X-Stream-Head` the answer carried — taken by the server BEFORE its query
 * ran, so an event can arrive twice but never be missed. The tab opens the stream at the LOWEST
 * head it holds, which is why every panel keeps its own.
 */
export async function read<T>(path: string): Promise<{ data: T; head: string | null }> {
  const response = await send(path, { method: "GET", headers: { Accept: "application/json" } });
  if (!response.ok) await trouble(response);
  return {
    data: (await response.json()) as T,
    head: response.headers.get("X-Stream-Head"),
  };
}

/**
 * One POST or DELETE. **Every mutation carries a client-minted `request_id`**, held for the life of
 * the act: a POST with no answer may or may not have been applied, and a re-send the reader asks
 * for carries the SAME id, which is what lets the server answer G3 *already yours* rather than
 * *changed under you* — the one moment a reader most needs to know whether their money moved.
 *
 * **The console never re-sends by itself.**
 */
export async function write<T>(
  path: string,
  payload: Record<string, unknown> | null,
  requestId: string,
  method: "POST" | "DELETE" = "POST",
): Promise<T> {
  const response = await send(path, {
    method,
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ ...(payload ?? {}), request_id: requestId }),
  });
  if (!response.ok) await trouble(response);
  return (await body(response)) as T;
}

/**
 * `POST /api/datapack`: one `.json` file, multipart. The answer is the server's sentence, success
 * or refusal; a 422 throws `Failed` with that sentence in its refusal.
 */
export async function upload<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append("file", file);
  const response = await send(path, { method: "POST", body: form, headers: { Accept: "application/json" } });
  if (!response.ok) await trouble(response);
  return (await body(response)) as T;
}

export async function whoami(): Promise<Who> {
  const { data } = await read<Who>("/auth");
  return data;
}

/** A fresh id per act. Held for the life of the act by whoever started it. */
export const newRequestId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `rq-${Date.now()}-${Math.random().toString(36).slice(2)}`;
